import { useRef, useState, useEffect, useMemo } from "react";
import { parseCsvFile } from "@/lib/csv";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, X, Users } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

const TEMPLATE = "grupo,num_socio,hora,hoyo\n1,1001,08:00,1\n1,1002,08:00,1\n1,1003,08:00,1\n2,1004,08:15,1\n2,1005,08:15,1\n";

const stripAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const HEADER_MAP = {
  grupo: "group", group: "group", gpo: "group",
  numsocio: "member_number", socio: "member_number", membernumber: "member_number", member: "member_number", num: "member_number",
  nombre: "name", name: "name", jugador: "name",
  hora: "time", time: "time", horario: "time", salida: "time",
  hoyo: "hole", hole: "hole", tee: "hole",
};
const normHeader = (h) => HEADER_MAP[stripAccents((h || "").toLowerCase()).replace(/[\s_]+/g, "")] || null;
const normName = (s) => stripAccents(String(s || "").toLowerCase()).replace(/[.,/]/g, " ").replace(/\s+/g, " ").trim();
const isNumeric = (s) => /^\d+$/.test(String(s || "").trim());

export default function TeeTimeCsvImport({ tid, cats, onImported }) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [roundNum, setRoundNum] = useState("1");
  const [categoryId, setCategoryId] = useState(cats[0]?.category_id || "");
  const [applying, setApplying] = useState(false);
  const [enrolled, setEnrolled] = useState(null); // Set of normalized enrolled names

  const selectedCat = cats.find((c) => c.category_id === categoryId);
  const maxRounds = selectedCat?.rounds || 1;

  // Load enrolled players of the selected category to pre-validate names
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch(`${API}/club/tournaments/${tid}/players`, { credentials: "include", headers: AUTH() });
        if (res.ok) {
          const pls = await res.json();
          const catName = selectedCat?.name;
          const set = new Set(pls.filter((p) => !catName || p.category_name === catName).map((p) => normName(p.name)));
          setEnrolled(set);
        }
      } catch { setEnrolled(null); }
    })();
  }, [open, categoryId, tid]); // eslint-disable-line

  const rowMatched = (r) => {
    if (!enrolled) return true;
    if (isNumeric(r.member_number)) return true;
    return enrolled.has(normName(r.name)) || enrolled.has(normName(r.member_number));
  };
  const unmatchedCount = useMemo(() => (rows || []).filter((r) => !rowMatched(r)).length, [rows, enrolled]); // eslint-disable-line

  const parseFile = (file) => {
    if (!file) return;
    parseCsvFile(file, { transformHeader: (h) => normHeader(h) || h }).then((res) => {
        const parsed = (res.data || []).map((r, i) => ({
          _i: i,
          group: (r.group || "").toString().trim(),
          member_number: (r.member_number || "").toString().trim(),
          name: (r.name || "").trim(),
          time: (r.time || "").trim(),
          hole: (r.hole || "").toString().trim(),
        })).filter((r) => r.member_number || r.name);
        if (parsed.length === 0) { toast.error("CSV sin filas válidas"); return; }
        setRows(parsed);
      }).catch(() => toast.error("No se pudo leer el CSV"));
  };

  const apply = async () => {
    if (!categoryId && cats.length > 0) return toast.error("Selecciona una categoría");
    setApplying(true);
    try {
      const res = await fetch(`${API}/club/tournaments/${tid}/teetimes/import-csv`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          round_num: parseInt(roundNum), category_id: categoryId || null,
          rows: rows.map((r) => ({ group: r.group, member_number: r.member_number, name: r.name, time: r.time, hole: r.hole })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al importar");
      toast.success(`Salidas importadas: ${data.applied} jugador(es) en ${data.teetime.total_groups} grupo(s)`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} fila(s) omitidas: ${data.skipped.slice(0, 3).map((s) => s.reason).join("; ")}${data.skipped.length > 3 ? "…" : ""}`);
      setRows(null); setOpen(false); onImported?.();
    } catch (e) { toast.error(e.message); }
    finally { setApplying(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "plantilla_salidas.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const grouped = {};
  (rows || []).forEach((r) => { (grouped[r.group || "?"] = grouped[r.group || "?"] || []).push(r); });

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="tt-csv-btn">
        <Upload className="w-4 h-4 mr-1" /> Importar salidas CSV
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="tt-csv-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-bold text-[#0F172A]">Importar salidas por CSV</h3>
              <button onClick={() => { setOpen(false); setRows(null); }} data-testid="tt-csv-close"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="px-5 py-4 overflow-auto flex-1 space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                {cats.length > 0 && (
                  <div>
                    <Label className="text-xs">Categoría</Label>
                    <Select value={categoryId} onValueChange={setCategoryId}>
                      <SelectTrigger className="h-9 w-48 text-sm" data-testid="tt-csv-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Ronda</Label>
                  <Select value={roundNum} onValueChange={setRoundNum}>
                    <SelectTrigger className="h-9 w-28 text-sm" data-testid="tt-csv-round"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: maxRounds }, (_, i) => i + 1).map((r) => <SelectItem key={r} value={String(r)}>Ronda {r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                  onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="tt-csv-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="tt-csv-upload"><Upload className="w-3.5 h-3.5 mr-1" /> Subir archivo</Button>
                <button onClick={downloadTemplate} className="text-xs text-[#1B4D3E] font-medium flex items-center gap-1 hover:underline mb-2" data-testid="tt-csv-template"><Download className="w-3.5 h-3.5" /> Plantilla</button>
              </div>

              {!rows ? (
                <p className="text-xs text-[#64748B]">Columnas: <code>grupo, num_socio, hora, hoyo</code>. Los jugadores con el mismo número de grupo quedan en la misma salida. Se identifica al jugador por número de socio <strong>o por nombre</strong> (sin importar acentos/mayúsculas).</p>
              ) : (
                <div>
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mb-2">Reemplaza las salidas existentes de la Ronda {roundNum} de esta categoría. {rows.length} jugador(es), {Object.keys(grouped).length} grupo(s).</p>
                  {unmatchedCount > 0 && (
                    <p className="text-xs text-red-700 bg-red-50 rounded px-3 py-2 mb-2" data-testid="tt-csv-unmatched">
                      ⚠️ {unmatchedCount} jugador(es) en rojo no coinciden con los inscritos de <strong>{selectedCat?.name}</strong>. Revisa el nombre/número de socio o inscríbelos primero; esas filas se omitirán.
                    </p>
                  )}
                  <div className="space-y-2" data-testid="tt-csv-preview">
                    {Object.entries(grouped).sort((a, b) => (a[0] > b[0] ? 1 : -1)).map(([g, members]) => (
                      <div key={g} className="border rounded-lg px-3 py-2">
                        <div className="text-xs font-bold text-[#1B4D3E] flex items-center gap-1"><Users className="w-3 h-3" /> Grupo {g} · {members[0].time || "—"} {members[0].hole ? `H${members[0].hole}` : ""}</div>
                        <div className="text-sm mt-0.5">
                          {members.map((m, i) => {
                            const ok = rowMatched(m);
                            return (
                              <span key={i} className={ok ? "text-[#0F172A]" : "text-red-600 font-medium"}>
                                {m.name || m.member_number}{!ok ? " ⚠️" : ""}{i < members.length - 1 ? ", " : ""}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <Button size="sm" variant="outline" onClick={() => { setOpen(false); setRows(null); }} data-testid="tt-csv-cancel">Cancelar</Button>
              <Button size="sm" disabled={!rows || applying} onClick={apply} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="tt-csv-apply">
                {applying ? "Importando..." : "Importar y reemplazar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
